import type { FC } from 'react'

import { cn } from '@/lib/utils'
import { useStore } from '@/lib/store'
import { sceneInput, uiStore } from '@/lib/scene-state'

const stops = [
    { id: 'hero', label: "Intro" },
    { id: 'power', label: "Power" },
    { id: 'lift', label: "Lift" },
    { id: 'safety', label: "Safety" },
    { id: 'engineering', label: "Engineering" },
    { id: 'configure', label: "Configure" }
]

/** Side rail on wide screens that shows where you are in the 3D story and jumps between stops. */
export const ChapterNav: FC = () => {
    const active = useStore(uiStore, state => state.activeChapter)
    const visible = active < 5

    const jump = (id: string) => {
        const element = document.getElementById(id)

        if (!element) return

        const rect = element.getBoundingClientRect()
        const top = Math.max(0, rect.top + window.scrollY + rect.height / 2 - window.innerHeight / 2)

        window.scrollTo({ top, behavior: sceneInput.reducedMotion ? 'auto' : 'smooth' })
    }

    return (
        <nav
            aria-label="Story chapters"
            className={cn(
                'fixed right-6 top-1/2 z-40 hidden -translate-y-1/2 transition-opacity duration-500 2xl:block',
                visible ? 'opacity-100' : 'pointer-events-none opacity-0'
            )}
        >
            <ol className='flex flex-col items-end gap-3.5'>
                {stops.map((stop, index) => (
                    <li key={stop.id}>
                        <button
                            type='button'
                            onClick={() => jump(stop.id)}
                            aria-current={active === index ? 'step' : undefined}
                            tabIndex={visible ? 0 : -1}
                            className='group flex items-center gap-3 rounded-full py-0.5 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-brand'
                        >
                            <span
                                className={cn(
                                    'font-mono text-[10px] uppercase tracking-[0.2em] transition-[opacity,color] duration-300',
                                    active === index ? 'text-white opacity-100' : 'text-white/60 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100'
                                )}
                            >
                                {stop.label}
                            </span>
                            <span
                                className={cn(
                                    'block h-px transition-all duration-500',
                                    active === index ? 'w-8 bg-brand' : 'w-4 bg-white/30 group-hover:w-6 group-hover:bg-white/60'
                                )}
                            />
                        </button>
                    </li>
                ))}
            </ol>
        </nav>
    )
}
